import { Layout, Spin } from 'antd';
import { useContext, useEffect, useMemo } from 'react';
import { useStore } from 'zustand';

import { HotelContext } from 'app/context/HotelContext/HotelContext';
import { ModernHotelContext } from 'app/context/HotelContext/ModernHotelContext';
import { useInventory } from 'app/hooks/data/core/extranet/inventory/useInventory';
import { ExtranetPackStatus } from 'app/typings/core/extranet/hotel/packs';
import { formatDateWithoutHour } from 'app/utils/dates';

import styles from './ExtranetInventory.module.scss';
import type { InventoryDependencies } from './InventoryStore';
import { InventoryTabItemName, inventoryStore } from './InventoryStore';
import { PackType } from './_components/Header/ActionsBar/InventoryToggle/InventoryToggle';
import { InventoryHeader } from './_components/Header/InventoryHeader/InventoryHeader';
import { PricingDrawer } from './_components/PricingDrawer/PricingDrawer';
import { ExperienceBulkEdit } from './_components/TabViews/ExperiencesTab/ExperienceBulkEdit/ExperienceBulkEdit';
import { ExperiencesTab } from './_components/TabViews/ExperiencesTab/ExperiencesTab';
import { PacksTab } from './_components/TabViews/PacksTab/PacksTab';
import { RoomBulkEdit } from './_components/TabViews/RoomsTab/RoomBulkEdit/RoomBulkEdit';
import { RoomsTab } from './_components/TabViews/RoomsTab/RoomsTab';
import { ChangesModal } from './_modals/ChangesModal/ChangesModal';
import {
  useInventoryActions,
  useInventoryQueryParams,
  useInventoryState,
} from './hooks';

const DEFAULT_DAYS = [1, 2, 3, 4, 5, 6, 7];

const PAGE = 'Inventory';

export const ExtranetInventory = () => {
  const { hotelId, hotelInfo, hasChannelManager } = useContext(HotelContext);

  const { packsList, experiencesList, originalsList } =
    useContext(ModernHotelContext);

  const { tab, timePeriod, endOfSaleDate } = useInventoryState();
  const { setInventory, setDependencies, setRawInventory } =
    useInventoryActions();

  useInventoryQueryParams();

  const setPackType = useStore(inventoryStore, (state) => state.setPackType);

  useEffect(() => {
    const filteredPacks = packsList.filter((pack) =>
      [ExtranetPackStatus.PUBLISHED, ExtranetPackStatus.UNPUBLISHED].includes(
        pack.status
      )
    );

    const hasNightStay = filteredPacks.some((pack) => !pack.isDaytime);
    const hasDayTime = filteredPacks.some((pack) => pack.isDaytime);

    if (!hasNightStay && hasDayTime) {
      setPackType(PackType.DAYTIME);
    }
  }, [packsList, setPackType]);

  const computePacksParameters = useMemo(
    () => ({
      saleEndDateString: endOfSaleDate,
      packData: packsList,
      experiences: experiencesList,
      originals: originalsList,
    }),
    [endOfSaleDate, packsList, experiencesList, originalsList]
  );

  const { inventory, rawInventory, isFetching } = useInventory(
    hotelInfo,
    {
      startDate: formatDateWithoutHour(timePeriod.startDate),
      endDate: formatDateWithoutHour(timePeriod.endDate),
    },
    computePacksParameters
  );

  const dependencies: InventoryDependencies = useMemo(
    () => ({
      saleEndDate: new Date(endOfSaleDate),
      packData: packsList,
      filteredExperiences: [...experiencesList, ...originalsList],
      inventoryInput: rawInventory ?? {},
      hasChannelManager,
      hasAutoInventory: !!hotelInfo?.stockScrapperEnabled,
      defaultNightRoomDiscount: hotelInfo?.defaultNightRoomDiscount,
      lastMinuteNightRoomDiscountRate:
        hotelInfo?.lastMinuteNightRoomDiscountRate,
    }),
    [
      endOfSaleDate,
      packsList,
      experiencesList,
      originalsList,
      rawInventory,
      hasChannelManager,
      hotelInfo?.stockScrapperEnabled,
      hotelInfo?.defaultNightRoomDiscount,
      hotelInfo?.lastMinuteNightRoomDiscountRate,
    ]
  );

  useEffect(() => {
    setDependencies(dependencies);
  }, [dependencies, setDependencies]);

  useEffect(() => {
    if (inventory !== undefined) {
      setInventory(inventory);
    }
  }, [inventory, setInventory]);

  useEffect(() => {
    setRawInventory(rawInventory ?? undefined);
  }, [rawInventory, setRawInventory]);

  const renderTabContent = () => {
    switch (tab.name) {
      case InventoryTabItemName.Packs:
        return <PacksTab />;
      case InventoryTabItemName.Rooms:
        return <RoomsTab />;
      case InventoryTabItemName.Experiences:
        return <ExperiencesTab />;
      default:
        return null;
    }
  };

  const renderBulkEditDrawer = () => {
    switch (tab.name) {
      case InventoryTabItemName.Rooms:
        return (
          <RoomBulkEdit
            initialValues={{
              days: DEFAULT_DAYS,
            }}
            currentRange={{
              start: timePeriod.startDate,
              end: timePeriod.endDate,
            }}
            hotelId={hotelId}
          />
        );
      case InventoryTabItemName.Experiences:
        return (
          <ExperienceBulkEdit
            initialValues={{
              days: DEFAULT_DAYS,
              stock: null,
            }}
            currentRange={{
              start: timePeriod.startDate,
              end: timePeriod.endDate,
            }}
            hotelId={hotelId}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Layout className={styles.extranetInventory}>
      <InventoryHeader page={PAGE} />
      <div
        className={styles.extranetInventoryContainer}
        key="extranet-inventory-container"
      >
        <Spin
          spinning={isFetching}
          size="default"
          className={styles.spinContainer}
        >
          {renderTabContent()}
        </Spin>
      </div>
      <ChangesModal />
      {renderBulkEditDrawer()}
      <PricingDrawer />
    </Layout>
  );
};
